<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import {
  fetchPaymentMethodPolicies,
  savePaymentMethodPolicy,
  evaluatePaymentMethodsForUser,
} from "@/services/backofficeClient";
import { ROLES } from "@/services/acl";

const METHOD_LABELS = {
  cardToCard: { label: "Card to card (کارت به کارت)", desc: "Card-to-card transfers reviewed by a PayManager." },
  tpayTelegram: { label: "TPay (Telegram)", desc: "TPay payments for Telegram users." },
  tpayWeb: { label: "TPay (Web)", desc: "TPay payments for web users." },
  paypal: { label: "PayPal", desc: "PayPal checkout." },
  crypto: { label: "Crypto (NowPayments)", desc: "Cryptocurrency via NowPayments." },
  premiumMoney: { label: "PremiumMoney vouchers", desc: "PremiumMoney code redemption." },
  uvoucher: { label: "Utopia vouchers (U-voucher)", desc: "Utopia voucher code redemption." },
  giftCard: { label: "Gift cards", desc: "Gift card code redemption." },
  telegramStars: { label: "Telegram Stars", desc: "Telegram Stars top-up. Also needs a Stars rate in Configuration." },
};

const CHANNEL_LABELS = {
  utopia: "Utopia",
  paypal: "PayPal",
  nowpayments: "Crypto (NowPayments)",
  premium_money: "PremiumMoney",
  bank_transfer: "Bank transfer",
  card_to_card: "Card to card",
  siz_pay: "SizPay",
  zarin_pal: "ZarinPal",
  tpay: "TPay",
  gift_card: "Gift card",
  telegram_stars: "Telegram Stars",
};

const PLAN_MATCH_OPTIONS = [
  { value: "any", label: "Has any paid plan" },
  { value: "none", label: "Has no paid plan" },
  { value: "any_of", label: "Has one of these plans" },
  { value: "none_of", label: "Has none of these plans" },
];

const store = useStore();
const canEdit = computed(() => store.getters.hasRole(ROLES.PAYMENT_METHOD_EDIT));

const loading = ref(true);
const loadError = ref("");
const policies = ref([]);
const topUpChannels = ref([]);
const plans = ref([]);

const selectedMethod = ref(null);
const draft = ref(null);
const originalSnapshot = ref("");
const saving = ref(false);
const formMessage = ref("");
const formMessageType = ref("success");

const evaluateUserId = ref("");
const evaluating = ref(false);
const evaluation = ref(null);
const evaluationError = ref("");

let nextRuleKey = 1;

const methodLabel = (method) => METHOD_LABELS[method]?.label || method;
const planLabel = (plan) => plan?.name?.en || plan?.name?.fa || Object.values(plan?.name || {})[0] || plan?.id;

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");
const fromDateInput = (value) => (value ? `${value}T00:00:00.000Z` : null);
const toOptionalInt = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

const ruleToDraft = (rule) => {
  const criteria = rule?.criteria || {};
  return {
    key: nextRuleKey++,
    id: rule?.id || "",
    name: rule?.name || "",
    isActive: rule?.isActive !== false,
    enabled: rule?.enabled === true,
    useUserIds: (criteria.userIds || []).length > 0,
    userIdsText: (criteria.userIds || []).join("\n"),
    useTopUpCount: Boolean(criteria.topUpCount),
    topUpMin: criteria.topUpCount?.min ?? "",
    topUpMax: criteria.topUpCount?.max ?? "",
    topUpChannels: [...(criteria.topUpCount?.channels || [])],
    useAccountCreatedAt: Boolean(criteria.accountCreatedAt),
    createdAfter: toDateInput(criteria.accountCreatedAt?.after),
    createdBefore: toDateInput(criteria.accountCreatedAt?.before),
    minAgeDays: criteria.accountCreatedAt?.minAgeDays ?? "",
    maxAgeDays: criteria.accountCreatedAt?.maxAgeDays ?? "",
    useActivePlan: Boolean(criteria.activePlan),
    planMatch: criteria.activePlan?.match || "any",
    planIds: [...(criteria.activePlan?.planIds || [])],
  };
};

const draftToRule = (rule) => ({
  id: rule.id,
  name: rule.name.trim(),
  isActive: rule.isActive,
  enabled: rule.enabled,
  criteria: {
    userIds: rule.useUserIds
      ? rule.userIdsText.split(/[\s,]+/).map((id) => id.trim()).filter(Boolean)
      : [],
    topUpCount: rule.useTopUpCount
      ? {
        min: toOptionalInt(rule.topUpMin),
        max: toOptionalInt(rule.topUpMax),
        channels: rule.topUpChannels,
      }
      : null,
    accountCreatedAt: rule.useAccountCreatedAt
      ? {
        after: fromDateInput(rule.createdAfter),
        before: fromDateInput(rule.createdBefore),
        minAgeDays: toOptionalInt(rule.minAgeDays),
        maxAgeDays: toOptionalInt(rule.maxAgeDays),
      }
      : null,
    activePlan: rule.useActivePlan
      ? {
        match: rule.planMatch,
        planIds: ["any_of", "none_of"].includes(rule.planMatch) ? rule.planIds : [],
      }
      : null,
  },
});

const draftPayload = computed(() =>
  draft.value
    ? {
      defaultEnabled: draft.value.defaultEnabled,
      rules: draft.value.rules.map(draftToRule),
    }
    : null
);
const isDirty = computed(
  () => draft.value && JSON.stringify(draftPayload.value) !== originalSnapshot.value
);

const loadDraft = (method) => {
  const policy = policies.value.find((p) => p.method === method);
  if (!policy) return;
  selectedMethod.value = method;
  draft.value = {
    defaultEnabled: policy.defaultEnabled === true,
    rules: (policy.rules || []).map(ruleToDraft),
  };
  originalSnapshot.value = JSON.stringify(draftPayload.value);
  formMessage.value = "";
};

const selectMethod = (method) => {
  if (isDirty.value && !window.confirm("Discard unsaved changes?")) return;
  loadDraft(method);
};

const loadPolicies = async () => {
  loading.value = true;
  loadError.value = "";
  try {
    const payload = await fetchPaymentMethodPolicies();
    policies.value = Array.isArray(payload?.methods) ? payload.methods : [];
    topUpChannels.value = Array.isArray(payload?.topUpChannels) ? payload.topUpChannels : [];
    plans.value = Array.isArray(payload?.plans) ? payload.plans : [];
  } catch (error) {
    console.error("Failed to fetch payment method policies", error);
    loadError.value = error?.payload?.message || error?.message || "Failed to load payment methods.";
  } finally {
    loading.value = false;
  }
};

const addRule = () => {
  draft.value.rules.push(ruleToDraft({ isActive: true, enabled: true, criteria: {} }));
};

const removeRule = (index) => {
  draft.value.rules.splice(index, 1);
};

const moveRule = (index, delta) => {
  const target = index + delta;
  const rules = draft.value.rules;
  if (target < 0 || target >= rules.length) return;
  [rules[index], rules[target]] = [rules[target], rules[index]];
};

const toggleInList = (list, value, checked) => {
  const index = list.indexOf(value);
  if (checked && index === -1) list.push(value);
  if (!checked && index !== -1) list.splice(index, 1);
};

const resetDraft = () => {
  loadDraft(selectedMethod.value);
};

const savePolicy = async () => {
  if (!selectedMethod.value || !draft.value) return;
  saving.value = true;
  formMessage.value = "";
  try {
    const method = selectedMethod.value;
    await savePaymentMethodPolicy(method, draftPayload.value);
    await loadPolicies();
    loadDraft(method);
    formMessage.value = "Saved. Changes apply to users immediately.";
    formMessageType.value = "success";
  } catch (error) {
    console.error("Failed to save payment method policy", error);
    formMessage.value = error?.payload?.message || error?.message || "Failed to save.";
    formMessageType.value = "danger";
  } finally {
    saving.value = false;
  }
};

const runEvaluation = async () => {
  const userId = evaluateUserId.value.trim();
  if (!userId) return;
  evaluating.value = true;
  evaluationError.value = "";
  evaluation.value = null;
  try {
    evaluation.value = await evaluatePaymentMethodsForUser(userId);
  } catch (error) {
    evaluationError.value = error?.payload?.message || error?.message || "Failed to evaluate.";
  } finally {
    evaluating.value = false;
  }
};

onMounted(async () => {
  await loadPolicies();
  if (policies.value.length) loadDraft(policies.value[0].method);
});
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12 col-lg-4">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0 d-flex justify-content-between align-items-start gap-2">
            <div>
              <h6 class="mb-1">Payment Methods</h6>
              <p class="text-xs text-secondary mb-0">Choose a payment method to manage who can use it.</p>
            </div>
            <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="loading" @click="loadPolicies">
              <i class="fas fa-sync me-2"></i>
              {{ loading ? "Refreshing..." : "Refresh" }}
            </button>
          </div>

          <div v-if="loading && !policies.length" class="card-body text-center py-5">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-else-if="loadError" class="card-body text-center py-5">
            <p class="text-sm">{{ loadError }}</p>
            <button class="btn btn-sm btn-success" type="button" @click="loadPolicies">Try again</button>
          </div>

          <div v-else class="card-body">
            <div class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Method</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Default
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Rules
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="policy in policies" :key="policy.method"
                    :class="policy.method === selectedMethod ? 'bg-light' : ''" style="cursor: pointer"
                    @click="selectMethod(policy.method)">
                    <td><span class="text-sm font-weight-bold">{{ methodLabel(policy.method) }}</span></td>
                    <td class="text-center">
                      <span class="badge text-xxs"
                        :class="policy.defaultEnabled ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                        {{ policy.defaultEnabled ? "On" : "Off" }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span class="text-xs">{{ (policy.rules || []).filter((r) => r.isActive).length }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="card shadow-sm mt-4">
          <div class="card-header pb-0 border-0">
            <h6 class="mb-1">Check a user</h6>
            <p class="text-xs text-secondary mb-0">See which methods a user gets right now, and which rule decided it.</p>
          </div>
          <div class="card-body">
            <form class="d-flex gap-2 mb-3" @submit.prevent="runEvaluation">
              <input v-model="evaluateUserId" class="form-control form-control-sm" type="text" placeholder="User ID" />
              <button class="btn btn-sm btn-success mb-0" type="submit" :disabled="evaluating || !evaluateUserId.trim()">
                {{ evaluating ? "Checking..." : "Check" }}
              </button>
            </form>
            <p v-if="evaluationError" class="text-xs text-danger mb-0">{{ evaluationError }}</p>
            <table v-if="evaluation" class="table align-items-center mb-0">
              <tbody>
                <tr v-for="decision in evaluation.decisions" :key="decision.method">
                  <td class="text-xs font-weight-bold">{{ methodLabel(decision.method) }}</td>
                  <td class="text-center">
                    <span class="badge text-xxs"
                      :class="decision.enabled ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                      {{ decision.enabled ? "On" : "Off" }}
                    </span>
                  </td>
                  <td class="text-xs text-secondary">
                    {{ decision.matchedRuleId ? decision.matchedRuleName || "Unnamed rule" : "Default" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-8 mt-4 mt-lg-0">
        <div v-if="draft" class="card shadow-sm">
          <div class="card-header pb-0 border-0">
            <h6 class="mb-1">{{ methodLabel(selectedMethod) }}</h6>
            <p class="text-xs text-secondary mb-0">{{ METHOD_LABELS[selectedMethod]?.desc }}</p>
          </div>

          <div class="card-body">
            <div v-if="formMessage" class="alert py-2 px-3"
              :class="formMessageType === 'danger' ? 'alert-danger' : 'alert-success'">
              <span class="text-sm">{{ formMessage }}</span>
            </div>

            <p class="text-xs text-secondary">
              Rules are checked from top to bottom. The first active rule whose conditions all match decides
              whether the user gets this method. If no rule matches, the default applies.
            </p>

            <div v-for="(rule, index) in draft.rules" :key="rule.key" class="border rounded p-3 mb-3"
              :class="rule.isActive ? '' : 'opacity-6'">
              <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
                <span class="badge bg-light text-dark border text-xxs">#{{ index + 1 }}</span>
                <input v-model="rule.name" class="form-control form-control-sm flex-grow-1" style="max-width: 280px"
                  type="text" placeholder="Rule name" :disabled="!canEdit || saving" />
                <select v-model="rule.enabled" class="form-select form-select-sm" style="max-width: 130px"
                  :disabled="!canEdit || saving">
                  <option :value="true">Turn on</option>
                  <option :value="false">Turn off</option>
                </select>
                <div class="form-check form-switch mb-0">
                  <input :id="`rule-active-${rule.key}`" v-model="rule.isActive" class="form-check-input"
                    type="checkbox" :disabled="!canEdit || saving" />
                  <label class="form-check-label text-xs" :for="`rule-active-${rule.key}`">Active</label>
                </div>
                <div v-if="canEdit" class="ms-auto d-flex gap-1">
                  <button class="btn btn-sm btn-outline-secondary mb-0 px-2" type="button" title="Move up"
                    :disabled="index === 0 || saving" @click="moveRule(index, -1)">
                    <i class="fas fa-arrow-up"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-secondary mb-0 px-2" type="button" title="Move down"
                    :disabled="index === draft.rules.length - 1 || saving" @click="moveRule(index, 1)">
                    <i class="fas fa-arrow-down"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-danger mb-0 px-2" type="button" title="Remove rule"
                    :disabled="saving" @click="removeRule(index)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>

              <p class="text-xs fw-semibold mb-2">When all of these match:</p>

              <div class="mb-3">
                <div class="form-check mb-1">
                  <input :id="`rule-users-${rule.key}`" v-model="rule.useUserIds" class="form-check-input"
                    type="checkbox" :disabled="!canEdit || saving" />
                  <label class="form-check-label text-sm" :for="`rule-users-${rule.key}`">User is one of</label>
                </div>
                <textarea v-if="rule.useUserIds" v-model="rule.userIdsText" class="form-control form-control-sm font-monospace"
                  rows="3" placeholder="One user ID per line" :disabled="!canEdit || saving"></textarea>
              </div>

              <div class="mb-3">
                <div class="form-check mb-1">
                  <input :id="`rule-topups-${rule.key}`" v-model="rule.useTopUpCount" class="form-check-input"
                    type="checkbox" :disabled="!canEdit || saving" />
                  <label class="form-check-label text-sm" :for="`rule-topups-${rule.key}`">
                    Number of previous successful top-ups
                  </label>
                </div>
                <div v-if="rule.useTopUpCount" class="ps-4">
                  <div class="row g-2 mb-2">
                    <div class="col-6 col-md-3">
                      <label class="form-label text-xs mb-0">At least</label>
                      <input v-model="rule.topUpMin" class="form-control form-control-sm" type="number" min="0"
                        :disabled="!canEdit || saving" />
                    </div>
                    <div class="col-6 col-md-3">
                      <label class="form-label text-xs mb-0">At most</label>
                      <input v-model="rule.topUpMax" class="form-control form-control-sm" type="number" min="0"
                        :disabled="!canEdit || saving" />
                    </div>
                  </div>
                  <label class="form-label text-xs mb-1">Count top-ups made with (none selected = all):</label>
                  <div class="d-flex flex-wrap gap-3">
                    <div v-for="channel in topUpChannels" :key="channel" class="form-check mb-0">
                      <input :id="`rule-channel-${rule.key}-${channel}`" class="form-check-input" type="checkbox"
                        :checked="rule.topUpChannels.includes(channel)" :disabled="!canEdit || saving"
                        @change="toggleInList(rule.topUpChannels, channel, $event.target.checked)" />
                      <label class="form-check-label text-xs" :for="`rule-channel-${rule.key}-${channel}`">
                        {{ CHANNEL_LABELS[channel] || channel }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <div class="form-check mb-1">
                  <input :id="`rule-created-${rule.key}`" v-model="rule.useAccountCreatedAt" class="form-check-input"
                    type="checkbox" :disabled="!canEdit || saving" />
                  <label class="form-check-label text-sm" :for="`rule-created-${rule.key}`">Account creation</label>
                </div>
                <div v-if="rule.useAccountCreatedAt" class="row g-2 ps-4">
                  <div class="col-6 col-md-3">
                    <label class="form-label text-xs mb-0">Created after</label>
                    <input v-model="rule.createdAfter" class="form-control form-control-sm" type="date"
                      :disabled="!canEdit || saving" />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label text-xs mb-0">Created before</label>
                    <input v-model="rule.createdBefore" class="form-control form-control-sm" type="date"
                      :disabled="!canEdit || saving" />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label text-xs mb-0">Min age (days)</label>
                    <input v-model="rule.minAgeDays" class="form-control form-control-sm" type="number" min="0"
                      :disabled="!canEdit || saving" />
                  </div>
                  <div class="col-6 col-md-3">
                    <label class="form-label text-xs mb-0">Max age (days)</label>
                    <input v-model="rule.maxAgeDays" class="form-control form-control-sm" type="number" min="0"
                      :disabled="!canEdit || saving" />
                  </div>
                  <p class="text-xxs text-secondary mb-0">Dates are UTC. Leave any field empty to skip it.</p>
                </div>
              </div>

              <div>
                <div class="form-check mb-1">
                  <input :id="`rule-plan-${rule.key}`" v-model="rule.useActivePlan" class="form-check-input"
                    type="checkbox" :disabled="!canEdit || saving" />
                  <label class="form-check-label text-sm" :for="`rule-plan-${rule.key}`">Active plan</label>
                </div>
                <div v-if="rule.useActivePlan" class="ps-4">
                  <select v-model="rule.planMatch" class="form-select form-select-sm mb-2" style="max-width: 260px"
                    :disabled="!canEdit || saving">
                    <option v-for="option in PLAN_MATCH_OPTIONS" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <div v-if="['any_of', 'none_of'].includes(rule.planMatch)" class="d-flex flex-wrap gap-3">
                    <div v-for="plan in plans" :key="plan.id" class="form-check mb-0">
                      <input :id="`rule-plan-${rule.key}-${plan.id}`" class="form-check-input" type="checkbox"
                        :checked="rule.planIds.includes(plan.id)" :disabled="!canEdit || saving"
                        @change="toggleInList(rule.planIds, plan.id, $event.target.checked)" />
                      <label class="form-check-label text-xs" :for="`rule-plan-${rule.key}-${plan.id}`">
                        {{ planLabel(plan) }}
                        <span v-if="!plan.isActive" class="text-secondary">(inactive)</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button v-if="canEdit" class="btn btn-sm btn-outline-secondary mb-4" type="button" :disabled="saving"
              @click="addRule">
              <i class="fas fa-plus me-2"></i>Add rule
            </button>

            <div class="border rounded p-3 mb-4 bg-gray-100">
              <div class="form-check form-switch mb-0">
                <input id="policy-default" v-model="draft.defaultEnabled" class="form-check-input" type="checkbox"
                  :disabled="!canEdit || saving" />
                <label class="form-check-label text-sm fw-semibold" for="policy-default">
                  Default: {{ draft.defaultEnabled ? "on" : "off" }} for everyone no rule matches
                </label>
              </div>
            </div>

            <div v-if="canEdit" class="d-flex align-items-center gap-2">
              <button class="btn btn-success mb-0" type="button" :disabled="saving || !isDirty" @click="savePolicy">
                {{ saving ? "Saving..." : "Save" }}
              </button>
              <button class="btn btn-outline-secondary mb-0" type="button" :disabled="saving || !isDirty"
                @click="resetDraft">
                Reset
              </button>
              <span v-if="isDirty" class="text-xs text-warning ms-2">Unsaved changes</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
